import type { Address, Hex } from "viem";
import type { PrivateKeyAccount } from "viem/accounts";
import {
  batchContext,
  batchSum,
  intentToJson,
  randomHex,
  receiptSigner,
  signIntent,
  type Intent,
  type IntentJson,
  type Receipt,
} from "@woodpecker/protocol";
import type { ServiceEntry } from "./chain";
import { HttpError } from "./clients";

export interface PendingAnswer {
  state?: string;
  call: string;
  receipt: Receipt;
}

/**
 * A paid write of the Console's own identity, outside a conversation (D75): the service holds the write pending and
 * answers with its receipt; the Console checks the receipt as the runner checks a gateway's (the client, the
 * identity, the token, the call, the actual against the price, the submitter's signature), signs one Intent with its
 * identity and asks the service to settle; the write is confirmed only then. A failed settle throws the service's
 * answer (402 settle_failed with its reason) and the write stays pending at the service for a resend.
 */
export async function paidWrite<T>(opts: {
  entry: ServiceEntry;
  wallet: Address;
  payer: PrivateKeyAccount;
  chainId: number;
  contractA: Address;
  pending: () => Promise<PendingAnswer>;
  settle: (calls: string[], signed: { intent: IntentJson; signature: Hex }) => Promise<T>;
}): Promise<{ result: T; receipt: Receipt }> {
  const { entry, payer } = opts;
  const p = await opts.pending();
  const r = p.receipt;
  const refuse = (detail: string) => new HttpError(400, "bad_receipt", { detail });
  // a service older than D75 applies the write at once and answers without a receipt: say so
  if (!r || (p.state && p.state !== "pending"))
    throw new HttpError(502, "no_receipt", {
      detail: `${entry.name} answered without a receipt: it runs code older than the paid writes`,
    });
  if (r.client.toLowerCase() !== opts.wallet.toLowerCase()) throw refuse("the receipt names another wallet");
  if (r.identity.toLowerCase() !== payer.address.toLowerCase()) throw refuse("the receipt names another identity");
  if (r.token.toLowerCase() !== entry.tokenAddress.toLowerCase()) throw refuse("the receipt names another token");
  if (r.call !== p.call) throw refuse("the receipt names another call");
  if (BigInt(r.actual) > BigInt(Math.ceil(entry.price.max)))
    throw refuse(`the receipt asks ${r.actual}, above the price`);
  if ((await receiptSigner(r))?.toLowerCase() !== entry.submitter.toLowerCase())
    throw refuse(`the receipt is not signed by the submitter of ${entry.name}`);
  const intent: Intent = {
    client: opts.wallet,
    identity: payer.address,
    token: r.token,
    payTo: entry.submitter,
    max: batchSum([r]),
    nonce: randomHex(32),
    context: batchContext([r]),
  };
  const signature = await signIntent(payer, intent, opts.chainId, opts.contractA);
  const result = await opts.settle([p.call], { intent: intentToJson(intent), signature });
  return { result, receipt: r };
}
